import { useEffect, useMemo } from "react";
import { hashKey, useQuery } from "@tanstack/react-query";
import { DirectorySort } from "@common/constants/counterparty";
import { GetCounterpartiesQuery } from "@common/schemas/counterparty.schema";
import { FiltersTypes } from "@common/types/filters/general";
import {
    counterpartiesQueryKeys,
    getCounterparties,
} from "@api/counterparty.api";
import { DirectoryFilters } from "@components/settings/Directory/lib";
import { useScope } from "@state/scopeContext.lib";
import { buildCounterpartiesTaskQuery } from "./counterpartiesTaskQuery";

interface UseCounterpartiesTaskRowsParams {
    queryScopeKey: string;
    scope: DirectoryFilters;
    counterpartySort: DirectorySort;
    viewFilters: FiltersTypes;
    resetRows: () => void;
    originalFilters?: FiltersTypes;
    stickyCounterpartiesIds?: string[];
    extraQueryFilters?: Partial<GetCounterpartiesQuery>;
    isEnabled?: boolean;
}

export const useCounterpartiesTaskRows = ({
    queryScopeKey,
    scope,
    counterpartySort,
    viewFilters,
    resetRows,
    originalFilters,
    stickyCounterpartiesIds,
    extraQueryFilters,
    isEnabled = true,
}: UseCounterpartiesTaskRowsParams) => {
    const { workspaceId } = useScope();

    const queryKey = useMemo(
        () => [
            ...counterpartiesQueryKeys.query(workspaceId ?? ""),
            "counterparties-task",
            queryScopeKey,
            scope,
            counterpartySort,
            viewFilters,
            originalFilters,
            stickyCounterpartiesIds,
            extraQueryFilters,
        ],
        [
            workspaceId,
            queryScopeKey,
            scope,
            counterpartySort,
            viewFilters,
            originalFilters,
            stickyCounterpartiesIds,
            extraQueryFilters,
        ],
    );
    const selectionResetKey = useMemo(() => hashKey(queryKey), [queryKey]);

    const counterparties = useQuery({
        queryKey,
        queryFn: () =>
            workspaceId
                ? getCounterparties(
                      workspaceId,
                      buildCounterpartiesTaskQuery({
                          scope,
                          sort: counterpartySort,
                          viewFilters,
                          originalFilters,
                          stickyCounterpartiesIds,
                          extraFilters: extraQueryFilters,
                      }),
                  )
                : undefined,
        enabled: !!workspaceId && isEnabled,
    });

    // A changed query can load a different row set; stale selection and
    // expansion would target rows the user can no longer see.
    useEffect(() => {
        resetRows();
    }, [selectionResetKey, resetRows]);

    const hasExpandableRows = useMemo(
        () =>
            counterparties.data?.data.some(
                (row) => (row.usageStats?.accountBreakdown ?? []).length > 1,
            ) ?? false,
        [counterparties.data],
    );

    return { counterparties, hasExpandableRows };
};
